import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

/** De que lado ainda há abas fora da vista — é onde a faixa esmaece. */
interface Overflow {
  start: boolean;
  end: boolean;
}

/**
 * Abas em pílula, como as do painel do `enamed-feedback-lp`, com
 * `role="tab"`/`aria-selected` para o leitor de tela anunciar "aba, 2 de 5,
 * selecionada"; o painel de conteúdo leva o `role="tabpanel"` em quem usa.
 *
 * **Telas estreitas (celular, tablet):** as abas nunca encolhem abaixo do
 * rótulo — quando não cabem, a faixa rola na horizontal, em vez de espremer
 * "Prova Oficial" numa fatia de 60px. Para a rolagem não passar despercebida:
 *
 * - a borda de onde há mais abas **esmaece** (`data-overflow-start|end`);
 * - a aba ativa **rola para o centro** ao abrir e a cada troca — quem chega
 *   por `?aba=diagnostico` vê a aba marcada, não a primeira;
 * - setas, Home e End trocam de aba (padrão WAI-ARIA de abas), com só a ativa
 *   na ordem do Tab.
 *
 * Com espaço sobrando (desktop), as abas crescem e dividem a pílula.
 *
 * **Abas com ícone** (`icon` nas opções, as principais do painel), como no
 * `enamed-feedback-lp`: no **celular** a aba é só o ícone; no **tablet**, o
 * ícone com o rótulo pequeno embaixo; no **desktop**, só o rótulo. O rótulo
 * fica sempre no DOM — escondido só para os olhos no celular (`.tab-label`) —,
 * porque é ele que nomeia o botão para o leitor de tela; um `aria-label` no
 * lugar duplicaria o texto em dois pontos que divergiriam.
 */
export function Tabs<T extends string>({
  idPrefix,
  label,
  options,
  active,
  onChange,
  size = 'md',
}: {
  /** Prefixo dos ids — dois grupos de abas na mesma tela não podem colidir. */
  idPrefix: string;
  label: string;
  options: {
    id: T;
    label: ReactNode;
    icon?: ReactNode;
    /**
     * "Em breve" (`preview` no Remote Config): a aba aparece com a etiqueta,
     * desabilitada de verdade — não abre, o teclado a pula e o leitor de tela
     * a anuncia como indisponível.
     */
    isPreview?: boolean;
  }[];
  active: T;
  onChange: (id: T) => void;
  size?: 'md' | 'sm';
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const withIcons = options.some((option) => option.icon);
  const [overflow, setOverflow] = useState<Overflow>({
    start: false,
    end: false,
  });

  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    // 1px de folga: o arredondamento do zoom deixa `scrollLeft` fracionário.
    const start = list.scrollLeft > 1;
    const end = list.scrollLeft + list.clientWidth < list.scrollWidth - 1;
    setOverflow((current) =>
      current.start === start && current.end === end ? current : { start, end },
    );
  }, []);

  // Mede ao montar e a cada mudança de largura (girar o tablet, redimensionar).
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [measure]);

  // A aba ativa no centro da faixa, sem mexer na rolagem vertical da página.
  useEffect(() => {
    const list = listRef.current;
    const tab = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !tab || list.scrollWidth <= list.clientWidth) return;
    const left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ left, behavior: 'smooth' });
    } else {
      list.scrollLeft = left;
    }
  }, [active]);

  const focusTab = (id: T) => {
    onChange(id);
    // O botão já existe: só muda de seleção. O foco acompanha a seleção.
    document.getElementById(tabId(idPrefix, id))?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Só as que abrem: a seta passa por cima de "Em breve".
    const openable = options.filter((option) => !option.isPreview);
    if (openable.length === 0) return;
    const index = openable.findIndex((option) => option.id === active);
    const last = openable.length - 1;
    const target =
      event.key === 'ArrowRight'
        ? index === last
          ? 0
          : index + 1
        : event.key === 'ArrowLeft'
          ? index === 0
            ? last
            : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (target === null) return;
    event.preventDefault();
    focusTab(openable[target].id);
  };

  return (
    <div
      className={[
        'tabs-scroller',
        size === 'sm' ? 'tabs-scroller-sm' : '',
        withIcons ? 'tabs-with-icons' : '',
      ].join(' ')}
      data-overflow-start={overflow.start || undefined}
      data-overflow-end={overflow.end || undefined}
    >
      <div
        ref={listRef}
        className={`tabs ${size === 'sm' ? 'tabs-sm' : ''}`}
        role="tablist"
        aria-label={label}
        onScroll={measure}
        onKeyDown={handleKeyDown}
      >
        {options.map((option) => {
          const isActive = option.id === active && !option.isPreview;
          return (
            <button
              key={option.id}
              type="button"
              role="tab"
              id={tabId(idPrefix, option.id)}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={`tab ${isActive ? 'is-active' : ''} ${option.isPreview ? 'is-preview' : ''}`}
              disabled={option.isPreview}
              onClick={() => onChange(option.id)}
            >
              {option.icon && (
                <span className="tab-icon" aria-hidden="true">
                  {option.icon}
                </span>
              )}
              <span className="tab-label">{option.label}</span>
              {option.isPreview && <span className="tab-soon">Em breve</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** O id do botão de uma aba, para o `aria-labelledby` do painel. */
export function tabId(prefix: string, id: string) {
  return `tab-${prefix}-${id}`;
}
